import { runAnalysisLens } from '@services/analysisItems';

/**
 * Check if an error is a 402 insufficient entitlements error.
 * These errors should be handled by the EntitlementContext modal, not a toast.
 * @param {object} error - The error object from axios (may be transformed by interceptor)
 * @returns {boolean} - True if this is a 402 insufficient entitlements error
 */
function isInsufficientEntitlementsError(error) {
  // Check for transformed error format (from axios interceptor)
  if (error?.status === 402 && error?.error_code === 'INSUFFICIENT') {
    return true;
  }
  // Check for original axios error format
  if (error?.response?.status === 402 && error?.response?.data?.error_code === 'INSUFFICIENT') {
    return true;
  }
  return false;
}
import {
  addPermissionToTeam,
  changeStudyOwner,
  inviteTeamMember,
  updateChatTitle,
} from '@services/member';
import { createOrganization } from '@services/organization';
import { createOrgProduct } from '@services/orgProduct';
import { createReport } from '@services/report';
import { dismissTutorial, skipWorkspacePayment } from '@services/user';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import {
  generateAIParticipantsResponses,
  generateInterviewAIParticipantsResponses,
} from '../services/ai-participant';
import {
  forgotPasswordService,
  registerUserService,
  SendOtp,
  verifyOtpService,
} from '../services/auth';
import {
  addNewCode,
  addNewParticipant,
  addSpeakerTag,
  addStudyTag,
  addStudyTagOption,
  addTag,
  analyzeInterview,
  assignCode,
  assignTagInTranscript,
  assignUnassignTagInTranscript,
  closeInterview,
  commitBlocks,
  commitMultiBlocks,
  copyTagsFromStudy,
  createInterview,
  createLinkBasedShareInterview,
  duplicateInterview,
  exportInterviewParticipantData,
  exportInterviewTranscripts,
  generateInterviewCodeBook,
  generateInterviewCodes,
  generateInterviewId,
  generateInterviewReport,
  generateInterviewThemes,
  generateMultiUploadUrl,
  generateUploadUrl,
  generateWelcomeMessage,
  likeDislikeChat,
  markAsCompleted,
  queueSemanticEmbedding,
  predictInterviewGuidelines,
  predictResearchQuestions,
  revokeLinkBasedShareInterview,
  saveAndAnalyzeInterviewAudioVideo,
  stopCollectingResponsesInterview,
  udpateCategoryDetails,
  udpateCodeDetails,
  udpateThemeDetails,
  updateSpeaker,
  updateSpeakerInfo,
  updateStudyTagAttribute,
  updateStudyTagOption,
  updateTranscriptName,
} from '../services/interview';
import { markAllAsReadNotification, markAsReadNotification } from '../services/notification';
import {
  estimateCredits,
  estimateCreditsForReAnalysis,
  upgradePlan,
  viewTransactions,
} from '../services/payment';
import {
  analyzeDynamicSurvey,
  analyzeQuestionElaboration,
  createLinkBasedShareSurvey,
  createTheme,
  duplicateSurvey,
  exportParticipantData,
  generateThemeDefination,
  postStudy,
  predictStudyTitle,
  revokeLinkBasedShareSurvey,
  sendAndGetMessage,
  stopCollectingResponsesSurvey,
  uploadAnalyzedStudy,
  uploadAndSaveSurvey,
  uploadStudies,
} from '../services/survey';
import {
  acceptInvitation,
  cancelSubscription,
  createCheckoutSession,
  createWorkspace,
  deleteWorkspace,
  getBillingPortal,
  inviteMember,
  joinWorkspace,
  reassignRole,
  removeMember,
  revokeInvitation,
  updateWorkspace,
  updateWorkspaceSettings,
} from '../services/workspace';
import { showAsyncTaskToast, showErrorToast, showSuccessToast } from '../utils/toast';

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllAsReadNotification(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => markAsReadNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });
};

export const useSendOtp = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await SendOtp(payload);
      return response;
    },
  });
};

export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await verifyOtpService(payload);
      return response;
    },
  });
};

export const useRegister = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await registerUserService(payload);
      return response;
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await forgotPasswordService(payload);
      return response;
    },
  });
};

export const useCreateSurvey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (transformedData) =>
      toast.promise(
        postStudy(transformedData),
        {
          loading: 'Saving study...',
          success: 'Study saved successfully!',
          error: 'Error occurred while saving study. Please try again later.',
          position: 'bottom-center',
        },
        {
          position: 'bottom-center', // Apply position here
        },
      ),

    onSuccess: (res) => {
      navigate('/dynamic-survey/configure', { state: { id: res.id } });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['draft-survey'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
  });
};
export const useUploadSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ formData, onProgress, signal }) =>
      uploadStudies({ formData, onProgress, signal }),
    onSuccess: (response) => {
      sessionStorage.setItem('AnalyzedStudyId', response.id);
      showSuccessToast('File successfully uploaded!');
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }, 2000);
    },
  });
};

export const useEstimateCredits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: estimateCredits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};
export const useEstimateCreditsForReAnalysis = () => {
  return useMutation({
    mutationFn: estimateCreditsForReAnalysis,
  });
};

export const useSurveyAnanyzeAndSave = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, payload }) => uploadAnalyzedStudy({ id, payload }),

    onSuccess: (response) => {
      toast.success(response.message);
      navigate('/');
    },

    onError: (error) => {
      alert(error);
    },
  });
};
export const useAnalyzeUploadData = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (rowId) => analyzeDynamicSurvey(rowId),
    onSuccess: () => {
      toast.success('Study has been analyzed successfully');
      navigate('/');
    },
    onError: (error) => {
      // Handle error if needed
      console.error('Error analyzing study:', error);
    },
  });
};
export const useUpgradePlan = () => {
  const workspaceId = localStorage.getItem('activeWorkspaceId');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, ...payload }) => upgradePlan({ workspaceId, ...payload }),

    onSuccess: (res) => {
      if (res.checkout_url) {
        const newTab = window.open(res.checkout_url, '_blank');
        newTab.focus();
        queryClient.invalidateQueries({ queryKey: ['me'] });
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });

        showSuccessToast(res.message || 'Plan upgraded successfully!');
      }
    },
    onError: (error) => {
      toast.error(
        error?.detail ||
          'This might be due to a pop-up being blocked by your browser. Please enable pop-ups and try again.',
      );
    },
  });
};

export const useUpgradePlan1 = () => {
  const navigate = useNavigate();
  const workspaceId = localStorage.getItem('activeWorkspaceId');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: upgradePlan,
    onSuccess: (res) => {
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });

        showSuccessToast(res.message || 'Plan upgraded successfully!');
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to upgrade plan. Please try again.');
      console.error('error', error);
    },
  });
};

export const useUploadAndSaveSurvey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ payload, workspaceId }) => uploadAndSaveSurvey(payload, workspaceId),
    onSuccess: () => {
      showAsyncTaskToast('Your study data has been uploaded and is being analyzed.');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
      navigate('/dynamic-surveys');
    },
  });
};

export const useAnalyzeSurvey = (id) => {
  const queryClient = useQueryClient();
  const workspaceId = localStorage.getItem('activeWorkspaceId') || '';
  return useMutation({
    mutationFn: () => analyzeDynamicSurvey(id, workspaceId),
    onSuccess: () => {
      showAsyncTaskToast('Survey analysis has started.');
      queryClient.invalidateQueries({ queryKey: ['single_survey', id] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};

export const useAnalyzeQuestionElaboration = (surveyId, sectionId, questionId, questionText) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => analyzeQuestionElaboration(surveyId, sectionId, questionId, questionText),
    onSuccess: () => {
      showAsyncTaskToast('Question analysis has started.');
      queryClient.invalidateQueries({ queryKey: ['single_survey', surveyId] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to start question analysis');
    },
  });
};
export const useCreateInterview = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (payload) =>
      toast.promise(createInterview(payload), {
        loading: 'Saving Study...',
        success: 'Study saved successfully!',
        position: 'bottom-center',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      navigate('/interviews');
    },
  });
};

export const useGenerateResponses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // No processId needed - backend uses atomic credit deduction
    mutationFn: ({ data }) => generateAIParticipantsResponses(data),
    onSuccess: (response) => {
      showAsyncTaskToast(response?.message || 'AI survey responses are being generated.');
      // Refresh credits display
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },
    // 402 errors handled by component's onError callback
  });
};
export const useInterviewGenerateResponses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // No processId needed - backend uses atomic credit deduction
    mutationFn: ({ data }) => generateInterviewAIParticipantsResponses(data),
    onSuccess: (response) => {
      showAsyncTaskToast(response?.message || 'AI interview responses are being generated.');
      // Refresh credits display
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },
    // 402 errors handled by component's onError callback
  });
};
export const useViewTransactions = () => {
  return useMutation({
    mutationFn: viewTransactions,
    onSuccess: (res) => {
      if (res.portal_url) {
        window.open(res.portal_url, '_blank');
      } else {
        window.open(res.url, '_blank');
      }
    },
  });
};

export const useAnalyzeInterview = (id) => {
  const queryClient = useQueryClient();
  const workspaceId = localStorage.getItem('activeWorkspaceId') || '';
  return useMutation({
    // workspace_id required for atomic credit deduction (unless regeneration with analysis_item_id)
    mutationFn: ({ id, analysis_lens_type, transcript_ids, analysis_level, analysis_item_id }) =>
      analyzeInterview(
        id,
        analysis_lens_type,
        transcript_ids,
        analysis_level,
        analysis_item_id,
        workspaceId,
      ),
    onSuccess: (res) => {
      showAsyncTaskToast(res.message || 'Study analysis has started.');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview', id] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },

    onError: (error) => {
      // Don't show toast for 402 insufficient entitlements - let EntitlementContext handle it
      if (!isInsufficientEntitlementsError(error)) {
        showErrorToast(error);
      }
    },
  });
};
export const useRunAnalyzeInterview = (id) => {
  const queryClient = useQueryClient();
  const workspaceId = localStorage.getItem('activeWorkspaceId') || '';
  return useMutation({
    // workspace_id required for atomic credit deduction
    mutationFn: ({ id, lensType, transcriptIds, analysis_level, custom_prompt }) =>
      runAnalysisLens(id, lensType, transcriptIds, analysis_level, custom_prompt, workspaceId),
    onSuccess: (res) => {
      showAsyncTaskToast(res.message || 'Study analysis has started.');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
      queryClient.invalidateQueries({ queryKey: ['single_interview', id] });
    },
    onError: (error) => {
      // Don't show toast for 402 insufficient entitlements - let EntitlementContext handle it
      if (!isInsufficientEntitlementsError(error)) {
        const message =
          error?.response?.data?.detail ||
          error?.message ||
          'Unable to start analysis. Please try again.';
        showErrorToast({ detail: { message } });
      }
    },
  });
};

export const useGenerateInterviewCodes = () => {
  return useMutation({
    mutationFn: (id) => generateInterviewCodes(id),
    onSuccess: (res) => showAsyncTaskToast(res.message || 'Code generation has started.'),
    onError: (error) => {
      if (!isInsufficientEntitlementsError(error)) {
        showErrorToast(error);
      }
    },
  });
};

export const useGenerateInterviewCodeBook = () => {
  return useMutation({
    mutationFn: (id) => generateInterviewCodeBook(id),
    onSuccess: (res) => showAsyncTaskToast(res.message || 'Codebook generation has started.'),
    onError: (error) => {
      if (!isInsufficientEntitlementsError(error)) {
        showErrorToast(error);
      }
    },
  });
};

export const useGenerateInterviewReport = () => {
  return useMutation({
    mutationFn: (id) => generateInterviewReport(id),
    onSuccess: (res) => showAsyncTaskToast(res.message || 'Report generation has started.'),
    onError: (error) => {
      if (!isInsufficientEntitlementsError(error)) {
        showErrorToast(error);
      }
    },
  });
};

export const useGenerateInterviewThemes = () => {
  return useMutation({
    mutationFn: (id) => generateInterviewThemes(id),
    onSuccess: (res) => showAsyncTaskToast(res.message || 'Theme generation has started.'),
    onError: (error) => {
      if (!isInsufficientEntitlementsError(error)) {
        showErrorToast(error);
      }
    },
  });
};
export const useGenerateInterviewId = () => {
  return useMutation({
    mutationFn: (data) => generateInterviewId(data),
    onError: (error) => showErrorToast(error.message || 'Failed to generate interview ID'),
  });
};
export const useGenerateUploadUrl = () => {
  return useMutation({
    mutationFn: (data) => generateUploadUrl(data),
  });
};

export const useMultiGenerateUploadUrl = () => {
  return useMutation({
    mutationFn: (data) => generateMultiUploadUrl(data),
  });
};
export const useCommitBlocks = () => {
  return useMutation({
    mutationFn: (data) => commitBlocks(data),
    onSuccess: () => {
      showSuccessToast('File successfully uploaded!');
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });
};
export const useMultiCommitBlocks = () => {
  return useMutation({
    mutationFn: (data) => commitMultiBlocks(data),
    onSuccess: () => {
      showSuccessToast('File successfully uploaded!');
    },
    onError: (error) => {
      showErrorToast(error);
    },
  });
};
export const useSaveAndAnalyzeInterviewAudioVideo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => saveAndAnalyzeInterviewAudioVideo(data),
    onSuccess: (res) => {
      showAsyncTaskToast(res.message || 'Audio/video processing has started.');

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },
    onError: (error) => {
      showErrorToast(error);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }, 2000);
    },
  });
};
export const useDismissTutorial = () => {
  const queryClient = new QueryClient();
  return useMutation({
    mutationFn: dismissTutorial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export const useSkipWorkspacePayment = () => {
  const queryClient = new QueryClient();

  return useMutation({
    mutationFn: skipWorkspacePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });

      window.location.reload();
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to skip payment. Please try again.');
    },
  });
};

export const useAddReport = () => {
  const queryClient = new QueryClient();
  return useMutation({
    mutationFn: createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
};

export const useAddTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => addTag({ payload, transcriptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interview-tags'] });
      showSuccessToast('Tag added successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useAddStudyTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId }) => addStudyTag({ payload, studyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      showSuccessToast('Tag added successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useAddStudyTagOption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, tagId }) => addStudyTagOption({ payload, studyId, tagId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      showSuccessToast('New tag value added successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useUpdateStudyTagOption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, tagId, valueId }) =>
      updateStudyTagOption({ payload, studyId, tagId, valueId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      showSuccessToast('Tag option updated successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useAssignUnAssignTagInTranscript = (type = 'assign') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, transcriptId, speakerId }) =>
      assignUnassignTagInTranscript({ payload, studyId, transcriptId, speakerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      showSuccessToast(`Tags ${type == 'assign' ? 'assigned' : 'unassigned'} successfully !`);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};

export const useMarkAsCompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, transcriptId, isCompleted }) =>
      markAsCompleted({ studyId, transcriptId, isCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['transcripts'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to mark transcript as completed. Please try again.');
    },
  });
};

export const useAssignTagInTranscript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, transcriptId, speakerId }) =>
      assignTagInTranscript({ payload, studyId, transcriptId, speakerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      queryClient.invalidateQueries({ queryKey: ['fetch-assigned-tag'] });
      showSuccessToast(`Tags assigned successfully !`);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useUpdateStudyTagAttribute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, tagId }) =>
      updateStudyTagAttribute({ payload, studyId, tagId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      showSuccessToast('Tag title updated successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};

export const useCopyTagsFromStudy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ targetStudyId, sourceStudyId }) =>
      copyTagsFromStudy({ targetStudyId, sourceStudyId }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['study-tags'] });
      if (data.tags_added > 0 || data.values_added > 0) {
        showSuccessToast(
          `Tags imported! ${data.tags_added} new tag${data.tags_added !== 1 ? 's' : ''}, ${data.values_added} new value${data.values_added !== 1 ? 's' : ''}.`,
        );
      } else {
        showSuccessToast('No new tags to import. All tags already exist.');
      }
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to import tags. Please try again.');
    },
  });
};

export const useDuplicateSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => duplicateSurvey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
  });
};

export const useStopCollectingResponsesSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ survey_id, disable_reason }) =>
      toast.promise(stopCollectingResponsesSurvey({ survey_id, disable_reason }), {
        loading: 'Stopping response collection...',
        success: 'Response collection stopped successfully!',
        error: (error) =>
          `Failed to stop collecting responses: ${error?.detail || 'Something went wrong'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries(['recent_studies']);
    },
  });
};

export const useDuplicateInterview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => duplicateInterview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
  });
};
export const useLinkBasedShareSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ surveyId, returnUrl }) => createLinkBasedShareSurvey({ surveyId, returnUrl }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['study-shares-survey', variables.surveyId] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to create share link. Please try again.');
    },
  });
};
export const useRevokeLinkBasedShareSurvey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ surveyId, token }) => revokeLinkBasedShareSurvey({ surveyId, token }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['study-shares-survey', variables.surveyId] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to revoke share link. Please try again.');
    },
  });
};
export const useLinkBasedShareInterview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewId, returnUrl }) =>
      createLinkBasedShareInterview({ interviewId, returnUrl }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        'team-share-member',
        variables.interviewId,
        'qualitative-studies',
      ]);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to create share link. Please try again.');
    },
  });
};

export const useRevokeLinkBasedShareInterview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewId, token }) => revokeLinkBasedShareInterview({ interviewId, token }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        'team-share-member',
        variables.interviewId,
        'qualitative-studies',
      ]);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to revoke share link. Please try again.');
    },
  });
};

export const useStopCollectingResponsesInterview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ interview_id, disable_reason }) =>
      toast.promise(stopCollectingResponsesInterview({ interview_id, disable_reason }), {
        loading: 'Stopping interview response collection...',
        success: 'Interview response collection stopped successfully!',
        error: (error) =>
          `Failed to stop interview responses: ${error?.detail || 'Something went wrong'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
  });
};

export const useCloseInterview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studyId) =>
      toast.promise(closeInterview(studyId), {
        loading: 'Closing interview...',
        success: 'Interview closed successfully. Any unused credits will be refunded.',
        error: (error) =>
          `Failed to close interview: ${error?.detail?.message || error?.message || 'Something went wrong'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};

export const useUpdateTranscriptName = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId, id }) =>
      updateTranscriptName({ payload, transcriptId, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      showSuccessToast('Transcript name updated successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update transcript name. Please try again.');
    },
  });
};

export const useQueueSemanticEmbedding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, transcriptIds }) =>
      toast.promise(queueSemanticEmbedding({ studyId, transcriptIds }), {
        loading: 'Preparing semantic search...',
        success: 'Semantic search is being prepared. Check back in a few minutes.',
        error: (error) =>
          `Failed to start semantic search: ${error?.detail || 'Something went wrong'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcripts'] });
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
    },
  });
};

export const useUpdateCode = (queryKey) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => udpateCodeDetails({ payload, transcriptId }),
    onSuccess: () => {
      if (queryKey) {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      }
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      // showSuccessToast('Code updated successfully !');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
  });
};

export const useUpdateCategory = (closeAllModalAfterSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload }) => udpateCategoryDetails({ payload }),
    onSuccess: () => {
      closeAllModalAfterSuccess();
      queryClient.invalidateQueries({ queryKey: ['codebook'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      showSuccessToast('Category updated successfully !');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
  });
};
export const useUpdateTheme = (closeAllModalAfterSuccess) => {
  return useMutation({
    mutationFn: ({ payload }) => udpateThemeDetails({ payload }),
    onSuccess: () => {
      closeAllModalAfterSuccess();

      showSuccessToast('Theme updated successfully !');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
  });
};

export const useAddSpeakerTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId, speakerId }) =>
      addSpeakerTag({ payload, transcriptId, speakerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      showSuccessToast('Tag successfully added!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add tag. Please try again.');
    },
  });
};
export const useUpdateSpeaker = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => updateSpeaker({ payload, transcriptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });

      showSuccessToast('Speaker updated successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update speaker. Please try again.');
    },
  });
};
export const useUpdateSpeakerInfo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId, speakerId }) =>
      updateSpeakerInfo({ payload, transcriptId, speakerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      showSuccessToast('Speaker information updated successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update speaker information. Please try again.');
    },
  });
};
export const useAddNewParticipant = (handleCloseNewParticipantPopover) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => addNewParticipant({ payload, transcriptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      handleCloseNewParticipantPopover();
      showSuccessToast('Participant added successfully !');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add participant. Please try again.');
    },
  });
};
export const useAddNewCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => addNewCode({ payload, transcriptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      showSuccessToast('New code successfully added!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add code. Please try again.');
    },
  });
};
export const useAssignCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, transcriptId }) => assignCode({ payload, transcriptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcript'] });
      queryClient.invalidateQueries({ queryKey: ['single_interview'] });
      showSuccessToast('Code assigned successfully!');
      toast.success(
        'Changes have been made. Re-run the analysis to ensure the updated data is reflected accurately.!',
      );
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to assign code. Please try again.');
    },
  });
};

export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, studyUrlType, returnUrl }) =>
      inviteTeamMember({ payload, studyId, studyUrlType, returnUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-share-member'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }, 2000);
      showSuccessToast('Team member successfully invited!');
    },
    onError: (error) => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }, 2000);
      toast.error(error?.message || 'Failed to invite team member. Please try again.');
    },
  });
};

export const useAddPermissionToTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, studyId, studyUrlType, returnUrl }) =>
      addPermissionToTeam({ payload, studyId, studyUrlType, returnUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-share-member'] });
      queryClient.invalidateQueries({ queryKey: ['team-member'] });
      showSuccessToast('Permission added successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || error?.message || 'Failed to add permission. Please try again.');
    },
  });
};

export const useChangeStudyOwner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, studyId, studyUrlType, email, returnUrl }) =>
      changeStudyOwner({ payload, studyId, studyUrlType, email, returnUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-share-member'] });
      queryClient.invalidateQueries({ queryKey: ['team-member'] });
      showSuccessToast('Owner successfully updated!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to change owner. Please try again.');
    },
  });
};
export const useGetWelcomeMessage = () => {
  return useMutation({
    mutationFn: ({ payload }) => generateWelcomeMessage(payload),
  });
};

export const usePredictStudyTitle = () => {
  return useMutation({
    mutationFn: (payload) => predictStudyTitle(payload),
  });
};
// Chat With Analysis
export const useSendAndGetMessage = () => {
  return useMutation({
    mutationFn: (payload) => sendAndGetMessage(payload),
  });
};
export const useLikeDislikeChat = (type = 'like') => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => likeDislikeChat(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history-id'] });
      showSuccessToast(`You ${type == 'like' ? 'liked' : 'disliked'} the chat`);
    },
    onError: (error) => {
      toast.error(error?.detail || `Failed to ${type == 'like' ? 'like' : 'dislike'} `);
    },
  });
};
export const useUpdateChatTitle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateChatTitle(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-history'] });
      showSuccessToast('Chat title updated successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add permission]. Please try again.');
    },
  });
};

// Workspace mutations
export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createWorkspace(payload),
    onSuccess: async (data) => {
      // Force immediate refetch of workspaces
      await queryClient.invalidateQueries({ queryKey: ['workspaces'], refetchType: 'active' });
      // Force refetch of the specific workspace
      await queryClient.invalidateQueries({
        queryKey: ['workspace', data.id],
        refetchType: 'active',
      });
      setTimeout(() => showSuccessToast('Workspace created successfully!'), 3000);
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to create workspace. Please try again.');
    },
  });
};

export const useCreateTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) => createTheme(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-question-theme'] });
      queryClient.invalidateQueries({ queryKey: ['survey_question_analysis'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to add theme. Please try again.');
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => updateWorkspace(workspaceId, payload),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Workspace updated successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update workspace. Please try again.');
    },
  });
};

export const useUpdateWorkspaceSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, settingsPayload }) =>
      updateWorkspaceSettings(workspaceId, settingsPayload),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Workspace settings updated successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update workspace settings. Please try again.');
    },
  });
};

export const useGenerateThemeDefination = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await generateThemeDefination(payload);
      return response;
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId) => deleteWorkspace(workspaceId),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries(['workspace', workspaceId]);
      showSuccessToast('Workspace deleted successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to delete workspace. Please try again.');
    },
  });
};

export const useInviteWorkspaceMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => inviteMember(workspaceId, payload),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });

      // Handle unregistered user response
      if (data?.is_registered === false) {
        showSuccessToast(
          `Invitation sent! ${data.message || 'The user will need to sign up first.'}`,
          '500px',
          6000,
        );
      } else {
        showSuccessToast('Member invited successfully!');
      }
    },
  });
};
export const useReassignRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => reassignRole(workspaceId, payload),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Member role updated successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update member role. Please try again.');
    },
  });
};

export const useAcceptWorkspaceInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, token }) => acceptInvitation(workspaceId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      // showSuccessToast('Invitation accepted successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to accept invitation. Please try again.');
    },
  });
};

/**
 * Hook for auto-joining a workspace using the GET /join endpoint.
 * This is a simplified flow that automatically accepts invitations on click.
 */
export const useJoinWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, token }) => joinWorkspace(workspaceId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (error) => {
      toast.error(error?.detail || error?.message || 'Failed to join workspace. Please try again.');
    },
  });
};

export const useRemoveWorkspaceMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, email }) => removeMember(workspaceId, email),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Member removed successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to remove member. Please try again.');
    },
  });
};

export const useRevokeWorkspaceInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, invitationId }) => revokeInvitation(workspaceId, invitationId),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Invitation revoked successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to revoke invitation. Please try again.');
    },
  });
};

export const useCreateWorkspaceCheckoutSession = () => {
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => createCheckoutSession(workspaceId, payload),
    onSuccess: (res) => {
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to create checkout session. Please try again.');
    },
  });
};

export const useWorkspaceBillingPortal = () => {
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => getBillingPortal(workspaceId, payload),
    onSuccess: (res) => {
      if (res.url) {
        window.location.href = res.url;
      }
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to access billing portal. Please try again.');
    },
  });
};

export const useCancelWorkspaceSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, payload }) => cancelSubscription(workspaceId, payload),
    onSuccess: (data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-subscription-usage', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      showSuccessToast('Subscription cancelled successfully!');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to cancel subscription. Please try again.');
      // queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
    },
  });
};

export const useExportParticipantData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId, studyType }) => {
      console.log(studyType);
      studyType === 'survey'
        ? exportParticipantData(studyId)
        : exportInterviewParticipantData(studyId);
    },
    onSuccess: () => {
      showAsyncTaskToast('Your data export has started.');
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },
    onError: (error) => {
      showErrorToast({
        detail: {
          message: error?.detail || 'Failed to export participant data. Please try again.',
        },
      });
    },
  });
};

export const useExportTranscriptData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studyId }) => exportInterviewTranscripts(studyId),
    onSuccess: () => {
      showAsyncTaskToast('Your transcript export has started.');
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      }, 2000);
    },
    onError: (error) => {
      showErrorToast({
        detail: {
          message: error?.detail || 'Failed to export transcript data. Please try again.',
        },
      });
    },
  });
};

export const usePredictResearchQuestions = () => {
  return useMutation({
    mutationFn: async (payload) => await predictResearchQuestions(payload),
    onError: (error) => {
      toast.error(error?.detail || 'Failed to generate research questions. Please try again.');
    },
  });
};

export const usePredictInterviewGuidelines = () => {
  return useMutation({
    mutationFn: async (payload) => await predictInterviewGuidelines(payload),
    onError: (error) => {
      toast.error(error?.detail || 'Failed to generate interview guidelines. Please try again.');
    },
  });
};

export const useCreateOrganization = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createOrganization(payload),
    onSuccess: () => {
      toast.success('Organization created successfully!');
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['limitation'] });
      navigate('/organizations');
    },
    onError: (error) => {
      toast.error(
        error?.detail || error?.message || 'Failed to create organization. Please try again.',
      );
    },
  });
};

export const useCreateOrgProduct = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createOrgProduct(payload),
    onSuccess: () => {
      showSuccessToast('Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['org_products'] });
      navigate('/products');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to create product. Please try again.');
    },
  });
};
